import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const RANCH_BG =
  "https://images.unsplash.com/photo-1701602029591-ed354736e28c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxob3JzZSUyMHJhbmNoJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4MjgyMTgxOXww&ixlib=rb-4.1.0&q=85";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={RANCH_BG} alt="Ranch" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <img src="/equimare-logo.png" alt="EquiMare Pro" className="h-11 w-11 rounded-xl object-cover ring-2 ring-white/30" />
            <span className="font-extrabold text-2xl">EquiMare Pro</span>
          </Link>
          <p className="text-2xl font-semibold max-w-md leading-snug">
            Your breeding spreadsheet, organized — daily rounds, teasing charts & vet-ready cards.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/equimare-logo.png" alt="EquiMare Pro" className="h-9 w-9 rounded-xl object-cover" />
            <span className="font-extrabold text-xl">EquiMare Pro</span>
          </Link>
          <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your farm's workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="you@farm.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Log in
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        New to EquiMare Pro?{" "}
        <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="go-signup">Start free trial</Link>
      </p>
    </AuthShell>
  );
}

export function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", farm_name: "", email: "", password: "", account_type: "farm" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isVet = form.account_type === "vet";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await signup(form);
    setLoading(false);
    if (res.ok) navigate(isVet ? "/dashboard/schedule" : "/dashboard/daily");
    else setError(res.error);
  };

  return (
    <AuthShell title="Start your free trial" subtitle="60 days free. No credit card required.">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label>Account type</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, account_type: "farm" }))}
              data-testid="signup-type-farm"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                !isVet ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground/70 hover:bg-accent"
              }`}
            >
              Farm
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, account_type: "vet" }))}
              data-testid="signup-type-vet"
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                isVet ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground/70 hover:bg-accent"
              }`}
            >
              Veterinarian
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {isVet
              ? "Manage multiple client farms, each with its own mares & foals."
              : "Track your own farm's mares, teasing & foals."}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="farm">{isVet ? "Practice name" : "Farm name"}</Label>
          <Input id="farm" value={form.farm_name} onChange={upd("farm_name")} data-testid="signup-farm" placeholder={isVet ? "Bluegrass Equine Vets" : "Green Acres Stud"} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Your name</Label>
          <Input id="name" value={form.name} onChange={upd("name")} data-testid="signup-name" placeholder="Jane Doe" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={upd("email")} required data-testid="signup-email" placeholder="you@farm.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={upd("password")} required minLength={6} data-testid="signup-password" placeholder="At least 6 characters" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        Already have an account?{" "}
        <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
